import { useCallback, useEffect } from 'react'
import type { JSX } from 'react'
import { useSelector } from 'react-redux'

import { useSplit } from '@buffer-mono/features'

import { useUser } from '../../../../../common/context/User'
import useAuthorizationInfo from '../../hooks/useAuthorizationInfo/useAuthorizationInfo'
import PaywallModal from '../PaywallPrompt/PaywallPrompt'

import {
  checkShouldShowPaywallUpgrade,
  setOAuthRedirectUriCookieFromAuthUrl,
} from '../../utils'
import ChannelConnectionsDialog from '../ChannelConnectionsDialog'
import { redirectToUrl } from '../../../../../common/utils/urls'
import {
  TrackingChannelEvent,
  triggerTrackingEventBasedOnAction,
} from '../../tracking'
import type { OrchestratorRootState } from '../../../../../common/events/types'
import type { OnRedirectParams } from '../../types'
import MultipleChannelsWarningModal from '../MultipleChannelsWarningModal/MultipleChannelsWarningModal'
import { MODALS } from '../../../../../common/types'
import type { Channel } from '../../../../../common/types'
import { userCanStartFreeTrial } from '../../../../../common/utils/user'
import { useModalManager } from '../../../../../components/ModalManager/hooks/useModalManager'

function PreAuthenticationRedirect(): JSX.Element | null {
  const user = useUser()
  const organizationId = user?.currentOrganization?.id
  const { openModal } = useModalManager()

  const canStartTrial = userCanStartFreeTrial(user)
  const { split: featureTrialSplit, isReady } = useSplit(
    'geid-feature-specific-trial',
  )
  const isInFeatureTrialSplit = isReady && featureTrialSplit?.treatment === 'on'

  const {
    selectedService,
    startConnection,
    isRefreshingChannelConnection,
    cta,
    selectedRefreshChannelId,
    connectChannelUpgrade,
    authRedirectUrl,
    showChannelStorefront,
    serviceRequiresFurtherConfiguration,
    startConversion,
    customChannelIdToConvert,
    authMethod,
    skipChannelCheck,
  } = useSelector((state: OrchestratorRootState) => state.channelConnections)

  const shouldShowPaywallModal =
    checkShouldShowPaywallUpgrade(
      user,
      selectedService,
      connectChannelUpgrade,
    ) &&
    !isRefreshingChannelConnection &&
    !startConversion

  /**
   * Scenarios which require further action
   * ---------------------
   * Some scenarios will require the user to perform another action
   * Before we can start fetching authentication data to start the channel connection
   * We do this check to prevent the automatic getAuthorizationInfo which is fired on first load
   *
   * Scenarios include:
   * - Free users requiring a Plan upgrade to connect more channels
   * - MP users who are trying to connect over their channel limit
   * - Users who are trying to connect a Service which requires extra setup: see list of channels defined in serviceRequiresFurtherConfiguration()
   */
  const requiresFurtherAction =
    shouldShowPaywallModal || serviceRequiresFurtherConfiguration

  const { url, handleGetAuthorizationInfo } = useAuthorizationInfo()

  const onRedirect = useCallback(
    (args: OnRedirectParams) => {
      const { redirectUrl, authMethod } = args

      if (selectedService && organizationId) {
        triggerTrackingEventBasedOnAction({
          account: user,
          event: isRefreshingChannelConnection
            ? TrackingChannelEvent.refresh
            : TrackingChannelEvent.connection,
          service: selectedService,
          cta,
          authMethod,
        })
      }

      // Store the redirect URI of the authorization URL the user is being
      // sent to, so the post-OAuth code exchange uses the exact same URI
      setOAuthRedirectUriCookieFromAuthUrl(redirectUrl)

      // Redirect to the URL after a small delay to ensure that tracking events are sent
      setTimeout(() => {
        redirectToUrl(redirectUrl)
      }, 300)
    },
    [selectedService, user, cta, isRefreshingChannelConnection, organizationId],
  )

  useEffect(() => {
    if (
      (startConnection || startConversion) &&
      selectedService &&
      !requiresFurtherAction &&
      !authRedirectUrl
    ) {
      handleGetAuthorizationInfo({
        service: selectedService,
        authMethod,
        isRefreshingConnection: startConversion
          ? false
          : isRefreshingChannelConnection,
        selectedRefreshChannelId,
      })
    }
    // `handleGetAuthorizationInfo` is included so this effect re-runs once the
    // Split SDK becomes ready: the callback no-ops until then (to avoid locking
    // in the legacy redirect URI) and changes identity when it resolves. The
    // `!authRedirectUrl` guard keeps that re-run from issuing a duplicate
    // request once a URL has been stored.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    startConnection,
    selectedService,
    isRefreshingChannelConnection,
    shouldShowPaywallModal,
    authRedirectUrl,
    handleGetAuthorizationInfo,
  ])

  useEffect(() => {
    if (startConversion && url && customChannelIdToConvert) {
      // Necessary to include the customChannelIdToConvert in state param for
      // when we get redirected back to Buffer from Instagram, so that we
      // have this channel id available for channel conversion
      const urlWithCustomChannelIdToConvert = `${url}&state=custom_channel_id_to_convert_${customChannelIdToConvert}`
      onRedirect({ redirectUrl: urlWithCustomChannelIdToConvert })
    }
  }, [startConversion, url, customChannelIdToConvert, onRedirect])

  // Open FeatureTrialModal for eligible users at channel limit
  useEffect(() => {
    const shouldShowModal =
      shouldShowPaywallModal && (selectedService || connectChannelUpgrade)

    if (shouldShowModal && canStartTrial && isInFeatureTrialSplit) {
      openModal({
        key: MODALS.featureTrial,
        data: {
          feature: 'channelLimit',
          cta: 'channelConnections-paywall-upgradePlan-1',
          upgradePathName: 'channelConnectionsPaywall-trial',
        },
      })
    }
  }, [
    shouldShowPaywallModal,
    selectedService,
    connectChannelUpgrade,
    canStartTrial,
    isInFeatureTrialSplit,
    openModal,
  ])

  /**
   * Paywall Modal/Prompt
   * ---------------------
   * Scenarios when this is shown:
   * - Free users requiring a Plan upgrade to connect more channels
   * - MP users who are trying to connect over their channel limit
   * - Your lifetimeConnectedChannels is over the limit (currently at 8)
   *
   * For users in the feature-specific trials split who can start a trial,
   * the FeatureTrialModal is opened via useEffect above using openModal
   */
  if (shouldShowPaywallModal && (selectedService || connectChannelUpgrade)) {
    // FeatureTrialModal is opened via useEffect for eligible users
    // Return null here since modal is managed centrally
    if (canStartTrial && isInFeatureTrialSplit) {
      return null
    }

    // Show standard PaywallModal for other users
    return <PaywallModal service={selectedService || undefined} />
  }

  if (isRefreshingChannelConnection && authRedirectUrl && !skipChannelCheck) {
    const userChannels = user.currentOrganization?.channels || []
    const numberOfChannels =
      userChannels.filter((channel) => {
        if (channel.service === selectedService) {
          if (channel.service === 'instagram') {
            return channel.type === 'business'
          }

          if (channel.service === 'facebook') {
            return channel.type === 'page'
          }

          return true
        }

        return false
      }).length || 0

    if (numberOfChannels > 1) {
      return (
        <MultipleChannelsWarningModal
          onContinue={(): void => {
            onRedirect({
              redirectUrl: authRedirectUrl,
            })
          }}
          channel={
            userChannels.find(
              (channel) => channel.id === selectedRefreshChannelId,
            ) as Channel
          }
        />
      )
    }
  }

  const channelConnectionsDialogActive =
    showChannelStorefront || requiresFurtherAction

  const shouldTriggerRedirectToChannelAuthentication =
    !channelConnectionsDialogActive && startConnection

  if (
    selectedService &&
    shouldTriggerRedirectToChannelAuthentication &&
    authRedirectUrl
  ) {
    onRedirect({
      redirectUrl: authRedirectUrl,
    })
    return null
  }

  return channelConnectionsDialogActive ? (
    <ChannelConnectionsDialog onRedirect={onRedirect} />
  ) : null
}

export default PreAuthenticationRedirect
